import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Snackbar,
  Box,
  Paper,
  Alert,
  Divider
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import BarChartIcon from '@mui/icons-material/BarChart';
import { createSession, getSession } from '../../services/sessionService';
import { neon } from '@neondatabase/serverless';
const sql = neon(process.env.REACT_APP_NEON_DATABASE_URL);

const SessionManager = ({ onSessionStart }) => {
  const [open, setOpen] = useState(true);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [newToken, setNewToken] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);

  const handleNewSession = async () => {
    try {
      const token = await createSession();
      setNewToken(token);
      setError('');
    } catch (err) {
      setError('Échec de la création d\'une nouvelle session');
    }
  };

  const handleContinueSession = async () => {
    if (!token.trim()) {
      setError('Veuillez entrer un jeton de session');
      return;
    }
    
    try {
      const session = await getSession(token);
      if (!session) {
        setError('Jeton de session invalide');
        return;
      }
      onSessionStart(token, session);
      setOpen(false);
    } catch (err) {
      setError('Échec de la récupération de la session');
    }
  };

  const handleStartWithNewToken = () => {
    onSessionStart(newToken);
    setOpen(false);
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(newToken);
      setShowSnackbar(true);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleTestResults = async () => {
    try {
      // Get all completed sessions
      const completedSessions = await sql`
        SELECT token 
        FROM sessions 
        WHERE completed = TRUE 
        ORDER BY created_at DESC 
        LIMIT 1
      `;

      if (completedSessions && completedSessions.length > 0) {
        const testSessionId = completedSessions[0].token;
        const session = await getSession(testSessionId);
        if (session) {
          onSessionStart(testSessionId, session);
          setOpen(false);
        } else {
          setError('Test session not found');
        }
      } else {
        setError('No completed sessions found in database');
      }
    } catch (err) {
      console.error('Error loading test session:', err);
      setError('Failed to load test session');
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={() => {}}
        PaperProps={{
          sx: { 
            minWidth: { xs: '90%', sm: '500px' },
            maxWidth: '600px',
            p: 0,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <Box sx={{ 
          p: 3, 
          background: 'linear-gradient(135deg, #6366F1 0%, #EC4899 100%)',
          color: 'white'
        }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            MasterFlow
          </Typography>
          <Typography variant="body1">
            Évaluez vos préférences et compétences
          </Typography>
        </Box>
        
        <DialogContent sx={{ p: 4, pt: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
            Commencer ou continuer une session
          </Typography>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}
          
          {newToken && (
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                my: 3, 
                bgcolor: 'rgba(99, 102, 241, 0.05)',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderRadius: 2,
                border: '1px solid rgba(99, 102, 241, 0.2)'
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                  Votre jeton de session :
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    wordBreak: 'break-all',
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                    p: 1.5,
                    borderRadius: 1
                  }}
                >
                  {newToken}
                </Typography>
              </Box>
              <IconButton 
                onClick={handleCopyToClipboard}
                size="small"
                color="primary"
                sx={{ 
                  ml: 1,
                  bgcolor: 'rgba(99, 102, 241, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(99, 102, 241, 0.2)',
                  }
                }}
                aria-label="copier dans le presse-papiers"
              >
                <ContentCopyIcon />
              </IconButton>
            </Paper>
          )}

          <TextField
            fullWidth
            margin="normal"
            label="Jeton de session"
            placeholder="Entrez votre jeton pour continuer une session existante"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            error={!!error && !newToken}
            variant="outlined"
            sx={{ 
              mt: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
          <Button 
            onClick={handleTestResults}
            variant="outlined" 
            color="secondary"
            startIcon={<BarChartIcon />}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Voir Résultats Test
          </Button>
          
          <Box>
            {newToken ? (
              <Button 
                onClick={handleStartWithNewToken} 
                variant="contained" 
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0px 4px 10px rgba(99, 102, 241, 0.3)',
                }}
              >
                Démarrer avec ce jeton
              </Button>
            ) : (
              <Button 
                onClick={handleNewSession} 
                variant="contained" 
                color="primary"
                startIcon={<AddCircleOutlineIcon />}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0px 4px 10px rgba(99, 102, 241, 0.3)',
                }}
              >
                Nouvelle session
              </Button>
            )}
            <Button 
              onClick={handleContinueSession}
              disabled={!token.trim()}
              variant="outlined"
              startIcon={<RestoreIcon />}
              sx={{ 
                ml: 2,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Continuer
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>
          Jeton copié dans le presse-papiers
        </Alert>
      </Snackbar>
    </>
  );
};

export default SessionManager;